import { withErrorBoundary } from "@sentry/nextjs";
import { FC } from "react";
import styled from "styled-components";
import { getPercentageValue } from "lib/utils/table/getPercentageValue";
import { Fund } from "./types";

interface Props {
  funds: Fund[];
  className?: string;
  // eslint-disable-next-line no-unused-vars
  onSelectFund: (fund: Fund) => void;
}

export const Table: FC<Props> = withErrorBoundary(({ funds, className = "", onSelectFund }) => {
  return (
    <StyledTable className={className}>
      <div className="Table__header">
        <div className="Table__Row Font_12_16_600">
          <div className="Table__cell">
            <span>Fund Name</span>
          </div>
          <div className="Table__cell Table__cell--expense">
            <span>Expense Ratio</span>
          </div>
          <div className="Table__cell">
            <span>1yr Return</span>
          </div>
          <div className="Table__cell">
            <span>5yr Return</span>
          </div>
          <div className="Table__cell">
            <span>10yr Return</span>
          </div>
          <div className="Table__cell">
            <span>YTD Return</span>
          </div>
        </div>
      </div>
      <div className="Table__body">
        {funds.map((fund) => (
          <div key={`${fund.id}`} className="Table__Row Table__Row__Styled Font_16_22_450">
            <div className="Table__cell">
              <button className="Table__symbol Color_dusk50" type="button" onClick={() => onSelectFund(fund)}>
                {fund.symbol}
              </button>
              <div className="Table__symbol__legend Color_tertiary Font_12_16_450">{fund.name}</div>
            </div>
            <div className="Table__cell Table__cell--expense" data-test-id={`fund - ${fund.name}`}>
              {getPercentageValue(fund?.expense_ratio?.scalar * 100)}
            </div>
            <div className="Table__cell">{getPercentageValue(fund?.one_year_return?.scalar * 100)}</div>
            <div className="Table__cell">{getPercentageValue(fund?.five_year_return?.scalar * 100)}</div>
            <div className="Table__cell">{getPercentageValue(fund?.ten_year_return?.scalar * 100)}</div>
            <div className="Table__cell">{getPercentageValue(fund?.ytd_return?.scalar * 100)}</div>
          </div>
        ))}
      </div>
    </StyledTable>
  );
}, {});

const StyledTable = styled.div`
  width: 100%;

  .Table__header {
    display: flex;
    font-weight: 600;
    font-size: 12px;
    padding: 12px 0;
    width: 100%;
  }

  .Table__Row {
    display: grid;
    grid-template-columns: 2fr;
    grid-auto-columns: 1fr; /* Define the width of subsequent columns */
    grid-auto-flow: column; /* Arrange items in columns */
    width: 100%;
    align-items: flex-start;
    text-align: right;
  }

  .Table__cell {
    position: relative;
  }

  .Table__cell:first-child {
    text-align: left;
  }

  .Table__symbol {
    cursor: pointer;
    padding-bottom: 4px;
    text-decoration: underline;
  }

  .Table__cell:empty {
    height: 100%;
  }

  .Table__cell:empty::before {
    content: "";
    display: inline-block;
    position: absolute;
    top: 11px; /* Half of the line height */
    right: 0;
    transform: translateY(-50%);
    width: 16px;
    height: 1px;
    background: ${({ theme }) => theme.colors.fog["80"]};
  }

  .Table__Row__Styled {
    border-top: 1px solid ${({ theme }) => theme.colors.fog["20"]};
    padding: 12px 0;
  }

  .Table__Row__Styled:first-child {
    border-color: ${({ theme }) => theme.colors.fog["30"]};
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Table__Row {
      grid-template-columns: 1fr;
      grid-auto-columns: minmax(50px, 1fr);
      gap: 8px;
    }

    .Table__header .Table__Row {
      grid-auto-columns: minmax(50px, 1fr);
    }

    .Table__header .Table__Row span {
      display: inline-block;
      width: 50px;
    }

    .Table__cell--expense,
    .Table__symbol__legend {
      display: none;
    }

    .Table__symbol {
      padding: 0;
    }

    .Table__cell {
      font-size: 14px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xs}px`}) {
    .Table__symbol {
      padding: 0;
    }

    .Table__Row {
      gap: 2px;
    }
  }
`;
